/**
 * Audiovisual Admin - Gestión de contenido audiovisual (videos de YouTube)
 * 
 * Permite a los administradores:
 * - Agregar videos de YouTube con título y descripción
 * - Editar información de videos existentes
 * - Eliminar videos
 * - Reordenar videos
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Loader2, Plus, Trash2, Save, Youtube } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

interface AudiovisualItem {
  id: string;
  title: string;
  description: string;
  youtubeUrl: string;
  order: number;
}

// Extraer video ID de URL de YouTube o iframe embed completo
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

// Normalizar input: si pegan iframe completo, extraer URL embed
function normalizeYoutubeInput(value: string): string {
  const trimmed = value.trim();
  const id = extractYouTubeId(trimmed);
  if (id) {
    return `https://www.youtube.com/embed/${id}`;
  }
  return trimmed;
}

export default function AudiovisualAdmin() {
  const { toast } = useToast();
  const { getAuthHeaders } = useAdminAuth();
  const [items, setItems] = useState<AudiovisualItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Cargar items una sola vez al montar (no incluir loadItems en deps: evitar refetch al redefinir la función)
  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  const loadItems = async () => {
    try {
      setIsLoading(true);
      const headers = await getAuthHeaders();
      const response = await fetch("/api/admin/audiovisual", { headers });
      
      if (!response.ok) {
        const error = await response.json();
        const msg = error.message || error.error || "Failed to load audiovisual";
        if (response.status === 503) {
          toast({
            title: "Base de datos no lista",
            description: msg,
            variant: "destructive",
          });
        }
        throw new Error(msg);
      }

      const data = await response.json();
      setItems(data.items || []);
    } catch (error) {
      console.error("Error loading audiovisual:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "No se pudieron cargar los videos",
        variant: "destructive",
      });
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = () => {
    const newItem: AudiovisualItem = {
      id: `video-${Date.now()}`,
      title: "",
      description: "",
      youtubeUrl: "",
      order: items.length,
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const updateItem = (id: string, updates: Partial<AudiovisualItem>) => {
    setItems(items.map(item => item.id === id ? { ...item, ...updates } : item));
  };

  const saveItems = async () => {
    // Validar que todos los videos tengan título y URL válida
    const invalidItems = items.filter(item => 
      !item.title.trim() || !item.youtubeUrl.trim() || !extractYouTubeId(item.youtubeUrl)
    );

    if (invalidItems.length > 0) {
      toast({
        title: "Error de validación",
        description: "Todos los videos deben tener título y URL válida de YouTube",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const headers = await getAuthHeaders();
      const response = await fetch("/api/admin/audiovisual", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save audiovisual");
      }

      toast({
        title: "Guardado",
        description: "Los videos se guardaron correctamente",
      });
    } catch (error) {
      console.error("Error saving audiovisual:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "No se pudieron guardar los videos",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <AdminPageHeader
        description="Videos de YouTube en /audiovisual. Pegá URL, youtu.be o embed; se normaliza a /embed/."
        actions={
          <>
            <Button onClick={addItem} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Agregar video
            </Button>
            <Button onClick={saveItems} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Guardar cambios
                </>
              )}
            </Button>
          </>
        }
      />

        {/* Videos */}
        {items.length === 0 ? (
          <Card className="border-border/80 shadow-sm">
            <CardContent className="pt-6 text-center text-muted-foreground">
              No hay videos. Haz clic en "Agregar Video" para comenzar.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((item, index) => {
              const videoId = extractYouTubeId(item.youtubeUrl);
              
              return (
                <Card key={item.id} className="border-border/80 shadow-sm">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <Youtube className="w-5 h-5 text-red-500" />
                        <CardTitle className="text-lg">Video {index + 1}</CardTitle>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      {/* Formulario */}
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor={`title-${item.id}`}>Título (h1) *</Label>
                          <Input
                            id={`title-${item.id}`}
                            value={item.title}
                            onChange={(e) => updateItem(item.id, { title: e.target.value })}
                            placeholder="Ej: Behind the Scenes - Sesión de Fotos"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`desc-${item.id}`}>Descripción</Label>
                          <Textarea
                            id={`desc-${item.id}`}
                            value={item.description}
                            onChange={(e) => updateItem(item.id, { description: e.target.value })}
                            placeholder="Texto que aparece arriba del video..."
                            rows={3}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`url-${item.id}`}>URL o iframe de YouTube *</Label>
                          <Textarea
                            id={`url-${item.id}`}
                            value={item.youtubeUrl}
                            onChange={(e) => {
                              const val = e.target.value;
                              const url = val.includes("<iframe") ? normalizeYoutubeInput(val) : val;
                              updateItem(item.id, { youtubeUrl: url || val });
                            }}
                            placeholder="URL: https://www.youtube.com/watch?v=... o pega el iframe completo"
                            rows={3}
                            className="font-mono text-sm"
                          />
                          {item.youtubeUrl && !videoId && (
                            <p className="text-xs text-destructive">
                              URL o iframe inválido. Usa: youtube.com/watch?v=..., youtu.be/... o pega el iframe de embed.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Preview */}
                      <div className="space-y-2">
                        <Label>Vista Previa</Label>
                        {videoId ? (
                          <div className="aspect-video rounded-lg overflow-hidden bg-black">
                            <iframe
                              width="560"
                              height="315"
                              src={`https://www.youtube.com/embed/${videoId}`}
                              title={item.title || "YouTube video"}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              referrerPolicy="strict-origin-when-cross-origin"
                              allowFullScreen
                              className="w-full h-full"
                            />
                          </div>
                        ) : (
                          <div className="aspect-video rounded-lg bg-muted flex items-center justify-center">
                            <div className="text-center text-muted-foreground">
                              <Youtube className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">Ingresa una URL válida para ver el preview</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Preview link */}
        <Card className="border-border/80 bg-muted/30 shadow-sm">
          <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium">Vista previa pública</p>
              <p className="text-xs text-muted-foreground">Abrí /audiovisual en otra pestaña.</p>
            </div>
            <Link to="/audiovisual" target="_blank" rel="noopener noreferrer">
              <Button variant="secondary">Ver audiovisual</Button>
            </Link>
          </CardContent>
        </Card>
    </div>
  );
}
